/* ============================================================================
 * ib-new-in-slider.js  -  window.IBNewInSlider
 *
 * HC - New In homepage slider (contract rev i, section 0.10) - 2026-10-15
 *
 * Browser-global ES5 IIFE for sections/ib-new-in-slider.liquid. No build step,
 * no optional chaining. Defer-safe: no top-level DOM work; boot() runs on
 * DOMContentLoaded (or on the next tick when the document is already parsed),
 * when every deferred script - launches-feed.js included - has run.
 *
 * Pipeline:  config -> fetch the bundled feed (once) -> IBLaunchFeed.validateFeed
 *            -> card-level REGION gating (cardVisible, contract 6.5)
 *            -> feed order, first `max` -> cards in their own colours
 *            -> a View all card first when some are left out -> scroll-snap carousel
 *
 *   - The feed is assets/launches-feed.json, the New In page's own asset.
 *     Rejected entries are skipped quietly. A feed that fails to load, fails
 *     validation as a whole, or leaves no visible launch hides the whole
 *     section (the root gets `hidden`); the homepage never shows an empty or
 *     error slider.
 *   - cardVisible is a copy of the page's (assets/ib-launches.js);
 *     tests/launches/ui-slider.test.js proves the two identical. Account
 *     gating stays on the New In page's modal, as on the page.
 *   - A card is one link: the New In page URL + #launch-<id>. The page
 *     scrolls to that card once its cards render (ib-launches.js jumpToHash).
 *   - Images: one <picture> per card. Its <source media="(max-width: 640px)">
 *     names the 2:3 portrait banner, its <img> the 3:2 banner, so the browser
 *     fetches only the shape in effect. A shape whose banner is null shows the
 *     first image whole on a white tile (data-ibns-landscape / -portrait =
 *     "tile"). Every image is lazy.
 *   - Carousel: native scroll-snap. Only page starts are snap points (as in the
 *     Promotions slider), arrows and dots move a page at a time, and smooth
 *     scrolling is off under prefers-reduced-motion. Width bands come from
 *     matchMedia; there are no window or document listeners on the storefront.
 *   - Cards never reference the promo engine, the promo feed or the modal.
 *   - Every text value is escaped with IBLaunchFeed.escapeHtml.
 *
 * Revision i, D-i5 (2026-10-15): each card wears its launch's colours exactly
 * as the New In page does. cardColours / accentOn / colourStyleValue are the
 * page's, copied verbatim (the test proves source and output identical): the
 * same ^#[0-9a-f]{6}$ and 4.5:1 re-check, the same accent adjustment, and the
 * values reach the DOM only as --ibl-card-bg / --ibl-card-ink /
 * --ibl-card-accent in the card's style attribute. A launch without colours,
 * or whose colours fail the re-check, keeps the house palette (the section
 * CSS). Nothing is ever sampled from a photo on the homepage.
 *
 * Revision i, D-i6 (2026-10-15): when the visible launches outnumber the
 * maximum, the slider ends with a View all card in the Promotions collage
 * style: one link to the New In page (no hash), named "View all N launches",
 * over a 3x3 grid of banners. Its tiles start with the first launch the slider
 * leaves out, wrap to the start of the visible launches and stop at nine,
 * skipping any launch without both banners. The slide is an .ibns-item marked
 * data-ibns-all (not data-ibns-item, so launch ids stay clean); items() takes
 * both, so it is a page stop for snap points, arrows, dots and focus.
 * Tile images are alt="" and never data-ibns-img, so bandChanged's alts stay
 * aligned with this.arts.
 *
 * Revision i, D-i7 (2026-10-16): the View all card leads the track instead of
 * ending it - the first slide and the first page stop, so the slider opens on
 * it. Its tiles still start with the launches the slider leaves out.
 *
 * D-i10 (2026-10-16, Noah): the View all card is the third slide, after two
 * launch cards, on both homepage sliders (this one and Promotions). Its place
 * is now the section's "View all card position" setting (first, third or
 * last; default third), so it can move without a code change.
 *
 * Revision j (2026-10-16, contract section 0.11): load-in motion. The section
 * CSS sweeps a light across the loading outline. Once the cards render,
 * intro() marks the root data-ibns-intro="wait" and an IntersectionObserver
 * turns it "in" when the card row first comes into view, so the cards rise in
 * one after another; watchImage() marks a slot whose photo the browser is
 * still fetching (img.complete === false) until it loads or fails. Without
 * IntersectionObserver, or under reduced motion, nothing is hidden.
 * ==========================================================================*/

window.IBNewInSlider = (function () {
  'use strict';

  /* ----------------------------------------------------------------------
   * 1. UI microcopy (English only, no locale keys by design) and constants
   * -------------------------------------------------------------------- */
  var STRINGS = {
    count: function (n) { return n === 1 ? '1 launch' : n + ' launches'; },
    kind: { brand: 'New brand', product: 'New product', collection: 'New collection' },
    page: function (i, n) { return 'Page ' + i + ' of ' + n; },
    viewAll: 'View all',
    viewAllName: function (n) { return 'View all ' + STRINGS.count(n); },
    more: function (n) { return '+' + n + ' more'; },
    browseAll: 'Browse them all'
  };
  var KICKER_JOIN = ' · ';

  var DEFAULTS = { max: 12, pageUrl: '/pages/new-in', buttonLabel: 'Discover', desktop: 3, tablet: 2, mobile: 1, gap: 16, viewAllPosition: 'third' };
  var VIEW_ALL_POSITIONS = ['first', 'third', 'last'];

  // Width bands: the Promotions slider's breakpoints (the section CSS uses the
  // same numbers). Up to 640px the slot is the 2:3 portrait banner.
  var PHONE = '(max-width: 640px)';
  var TABLET = '(max-width: 980px)';

  // Card geometry for `sizes`, mirroring the section CSS: cards share
  // min(100vw, 1280px) less clamp(20px, 4vw, 56px) a side, less the gaps (one
  // per card with the peek, else one fewer) and the 60px peek. Phones use a
  // 12px gap and tablets 16px, whatever the gap setting (as Promotions does).
  var MAX_WIDTH = 1280;
  var WIDE_PADDING = 112;
  var PHONE_PADDING = 40;
  var PHONE_MAX = 640;
  var PHONE_CARD_MAX = 360;
  var PEEK = 60;
  var PHONE_GAP = 12;
  var TABLET_GAP = 16;
  var IMAGE_WIDTHS = [360, 540, 720, 900, 1080, 1440];
  var CARD_SRC_WIDTH = 720;
  // View all card (D-i6): a 3x3 collage, each tile about a third of a card.
  var COLLAGE_COLUMNS = 3;
  var COLLAGE_TILES = 9;
  var TILE_WIDTHS = [180, 360, 540];
  var TILE_SRC_WIDTH = 360;
  // Every slide: the launch cards and the View all card (D-i6).
  var SLIDE = '[data-ibns-item], [data-ibns-all]';
  // A programmatic page move holds the dots on its target while it animates.
  var LOCK_MS = 800;

  var ARROW = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true" focusable="false"><path d="M5 12h14M13 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var hasOwn = Object.prototype.hasOwnProperty;

  function feed() { return window.IBLaunchFeed; }
  function esc(value) { return feed().escapeHtml(value); }
  function closest(el, selector) {
    return (el && el.closest) ? el.closest(selector) : null;
  }
  function matches(query) {
    return typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
  }
  function reducedMotion() {
    return matches('(prefers-reduced-motion: reduce)');
  }
  function now() {
    return (window.performance && typeof window.performance.now === 'function') ? window.performance.now() : new Date().getTime();
  }
  // Rev j: a slot shimmers while its photo is still loading. Only an image the
  // browser reports as incomplete is marked, and the mark goes on load or
  // error, so a photo is never left hidden (the page's rule).
  function watchImage(img) {
    if (!img || img.complete !== false || typeof img.addEventListener !== 'function') return;
    var slot = closest(img, '.ibns-slot');
    if (!slot) return;
    slot.setAttribute('data-ibns-loading', 'wait');
    var done = function () {
      img.removeEventListener('load', done);
      img.removeEventListener('error', done);
      slot.removeAttribute('data-ibns-loading');
    };
    img.addEventListener('load', done);
    img.addEventListener('error', done);
  }
  function warn(message, detail) {
    if (typeof console !== 'undefined' && console && console.warn) console.warn(message, detail);
  }

  /* ----------------------------------------------------------------------
   * 2. Card-level access: REGION ONLY (contract 6.5) - identical to the page
   * -------------------------------------------------------------------- */
  function cardVisible(launch, customer) {
    var policy = window.IBAccessPolicy;
    if (!policy || typeof policy.evaluatePromotionAccess !== 'function') {
      return launch.restrictedRegions.length === 0;            // fail closed on regions only
    }
    var r = policy.evaluatePromotionAccess(
      { restrictedRegions: launch.restrictedRegions, matrixOverridable: launch.matrixOverridable },
      customer, { pricingRestrictionsEnabled: false });
    return !(r && r.restrictionType === 'region_restricted');
  }

  /* ----------------------------------------------------------------------
   * 2b. Card colours (D-i5): the page's colour rules, copied verbatim from
   *     ib-launches.js (contract 0.6) - tests/launches/ui-slider.test.js
   *     proves each function identical to the page's in source and output.
   *     A launch's colours pass the page's re-check or the card keeps the
   *     house palette (ib-new-in-slider.liquid). No colour is ever read from
   *     a photo on the homepage.
   * -------------------------------------------------------------------- */
  var HEX = /^#[0-9a-f]{6}$/;
  function rgbOf(hex) {
    var n = parseInt(hex.slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  function luminance(rgb) {
    var w = [0.2126, 0.7152, 0.0722];
    var sum = 0;
    for (var i = 0; i < 3; i++) {
      var v = rgb[i] / 255;
      v = v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
      sum += v * w[i];
    }
    return sum;
  }
  function contrastRatio(a, b) {
    var x = luminance(a);
    var y = luminance(b);
    return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
  }
  function toHex(rgb) {
    var out = '#';
    for (var i = 0; i < 3; i++) out += (rgb[i] < 16 ? '0' : '') + rgb[i].toString(16);
    return out;
  }
  // v3 numColor: keep the accent's hue, step it toward black (white on a dark
  // card) until it reaches 4.6:1 on the background (a hair over 4.5:1).
  function accentOn(accent, background) {
    var a = rgbOf(accent);
    var b = rgbOf(background);
    var to = luminance(b) > 0.18 ? 0 : 255;
    for (var s = 0; s <= 40; s++) {
      var c = [];
      for (var i = 0; i < 3; i++) c.push(Math.round(a[i] + (to - a[i]) * s / 40));
      if (contrastRatio(c, b) >= 4.6) return toHex(c);
    }
    return to ? '#ffffff' : '#000000';
  }
  // The page's own re-check: three lowercase #rrggbb strings, text on
  // background at 4.5:1 or more. Anything else returns null (house palette).
  function cardColours(launch) {
    var c = launch && launch.colours;
    if (!c || typeof c !== 'object') return null;
    var bg = c.background;
    var ink = c.text;
    var accent = c.accent;
    if (typeof bg !== 'string' || typeof ink !== 'string' || typeof accent !== 'string') return null;
    if (!HEX.test(bg) || !HEX.test(ink) || !HEX.test(accent)) return null;
    if (contrastRatio(rgbOf(ink), rgbOf(bg)) < 4.5) return null;
    return { bg: bg, ink: ink, accent: accent, mark: accentOn(accent, bg) };
  }

  // The only colour values written to the DOM: three re-checked properties.
  function colourStyleValue(colours, accent) {
    return '--ibl-card-bg:' + colours.bg + ';--ibl-card-ink:' + colours.ink + ';--ibl-card-accent:' + accent;
  }

  /* ----------------------------------------------------------------------
   * 3. Settings, card content and geometry (pure)
   * -------------------------------------------------------------------- */
  function intIn(value, lo, hi, fallback) {
    var n = parseInt(value, 10);
    if (isNaN(n)) return fallback;
    return n < lo ? lo : (n > hi ? hi : n);
  }

  // The New In page URL without any hash: a site path or an http(s) URL, else
  // the default page.
  function pageBase(url) {
    var s = typeof url === 'string' ? url.replace(/^\s+|\s+$/g, '') : '';
    var hash = s.indexOf('#');
    if (hash > -1) s = s.slice(0, hash);
    return /^(\/(?!\/)|https?:\/\/)/i.test(s) ? s : DEFAULTS.pageUrl;
  }

  function options(cfg) {
    cfg = cfg || {};
    var cards = cfg.cards || {};
    var label = typeof cfg.buttonLabel === 'string' ? cfg.buttonLabel.replace(/^\s+|\s+$/g, '') : '';
    return {
      max: intIn(cfg.max, 1, 30, DEFAULTS.max),
      pageUrl: pageBase(cfg.pageUrl),
      buttonLabel: label || DEFAULTS.buttonLabel,
      desktop: intIn(cards.desktop, 1, 5, DEFAULTS.desktop),
      tablet: intIn(cards.tablet, 1, 4, DEFAULTS.tablet),
      mobile: intIn(cards.mobile, 1, 2, DEFAULTS.mobile),
      gap: intIn(cfg.gap, 0, 48, DEFAULTS.gap),
      peek: cfg.peek !== false,
      viewAllPosition: VIEW_ALL_POSITIONS.indexOf(cfg.viewAllPosition) > -1 ? cfg.viewAllPosition : DEFAULTS.viewAllPosition
    };
  }

  // Where the View all card goes among the launch cards (D-i10).
  function viewAllIndex(position, cardCount) {
    if (position === 'first') return 0;
    if (position === 'last') return cardCount;
    return Math.min(2, cardCount);
  }

  function cardHref(opts, launch) {
    return opts.pageUrl + '#launch-' + launch.id;
  }

  // "BRAND · New collection": the brand and the fixed kind label; the brand
  // alone without a kind label; '' when there is neither.
  function kickerText(launch) {
    var parts = [];
    if (launch.brand) parts.push(launch.brand);
    if (hasOwn.call(STRINGS.kind, launch.kind)) parts.push(STRINGS.kind[launch.kind]);
    return parts.join(KICKER_JOIN);
  }

  // What each slot shape shows: its banner, or the first image whole on a
  // white tile when that banner is null. alt = the banner's alt || title, or
  // for a tile the first image's alt || title (contract 6.4).
  function slotArt(launch) {
    var first = launch.images[0];
    function shape(banner) {
      if (banner) return { url: banner.url, alt: banner.alt || launch.title, tile: false };
      return { url: first.url, alt: first.alt || launch.title, tile: true };
    }
    return { landscape: shape(launch.banner), portrait: shape(launch.portraitBanner) };
  }

  function gapsPx(n, gap, peek) {
    return (peek ? n : n - 1) * gap + (peek ? PEEK : 0);
  }
  function cardCalc(n, gap, peek, space, part) {
    return 'calc((' + space + ' - ' + gapsPx(n, gap, peek) + 'px) / ' + (n * part) + ')';
  }
  // `sizes` for each slot shape: the portrait <source> applies up to 640px,
  // where a card is at most 360px wide (the section CSS); the landscape <img>
  // covers the tablet and desktop bands and the fixed card width beyond the
  // 1280px section. `part` splits each width: 1 for a card's slot (the
  // default), COLLAGE_COLUMNS for a View all tile (tileSizes, D-i6).
  function slotSizes(opts, part) {
    part = part || 1;
    var wide = Math.round((MAX_WIDTH - WIDE_PADDING - gapsPx(opts.desktop, opts.gap, opts.peek)) / opts.desktop / part);
    var capFrom = PHONE_CARD_MAX * opts.mobile + PHONE_PADDING + gapsPx(opts.mobile, PHONE_GAP, opts.peek);
    var phone = cardCalc(opts.mobile, PHONE_GAP, opts.peek, '100vw - ' + PHONE_PADDING + 'px', part);
    return {
      portrait: (capFrom <= PHONE_MAX ? '(min-width: ' + capFrom + 'px) ' + Math.round(PHONE_CARD_MAX / part) + 'px, ' : '') + phone,
      landscape: '(max-width: 980px) ' + cardCalc(opts.tablet, TABLET_GAP, opts.peek, '92vw', part) +
        ', (max-width: 1280px) ' + cardCalc(opts.desktop, opts.gap, opts.peek, '92vw', part) +
        ', ' + wide + 'px'
    };
  }
  function tileSizes(opts) {
    return slotSizes(opts, COLLAGE_COLUMNS);
  }

  // The View all card's tiles (D-i6): feed order from the first launch the
  // slider leaves out (index `start`), wrapping to the start of the visible
  // launches, at most nine. A launch without both banners is skipped, so a
  // tile never shows the white-tile fallback.
  function collageTiles(visible, start) {
    var tiles = [];
    for (var k = 0; k < visible.length && tiles.length < COLLAGE_TILES; k++) {
      var launch = visible[(start + k) % visible.length];
      if (launch.banner && launch.portraitBanner) tiles.push(launch);
    }
    return tiles;
  }

  // The first card index of each page: every perView cards, plus the last full
  // page when the count does not divide evenly (as in the Promotions slider).
  function pageStarts(count, perView) {
    if (!count) return [];
    var per = Math.max(1, Math.min(perView, count));
    var last = count - per;
    var starts = [];
    for (var i = 0; i <= last; i += per) starts.push(i);
    if (starts[starts.length - 1] !== last) starts.push(last);
    return starts;
  }

  /* ----------------------------------------------------------------------
   * 4. Render-template layer (markup only; all values escaped here)
   * -------------------------------------------------------------------- */
  var templates = {
    // One <picture>: the phone source names the 2:3 image, the <img> the 3:2
    // one, so only the shape in effect is fetched. The kicker sits over the
    // top of the image on a soft scrim, as the Promotions kicker does.
    slot: function (launch, ctx) {
      var F = feed();
      var land = ctx.art.landscape;
      var port = ctx.art.portrait;
      var current = ctx.phone ? port : land;
      var kicker = kickerText(launch);
      return '<div class="ibns-slot" data-ibns-landscape="' + (land.tile ? 'tile' : 'banner') + '"' +
        ' data-ibns-portrait="' + (port.tile ? 'tile' : 'banner') + '">' +
        '<picture>' +
        '<source media="' + PHONE + '" srcset="' + esc(F.imageSrcset(port.url, IMAGE_WIDTHS)) + '"' +
        ' sizes="' + esc(ctx.sizes.portrait) + '">' +
        '<img class="ibns-img" data-ibns-img src="' + esc(F.imageUrl(land.url, CARD_SRC_WIDTH)) + '"' +
        ' srcset="' + esc(F.imageSrcset(land.url, IMAGE_WIDTHS)) + '"' +
        ' sizes="' + esc(ctx.sizes.landscape) + '"' +
        ' alt="' + esc(current.alt) + '" loading="lazy" decoding="async">' +
        '</picture>' +
        '<span class="ibns-scrim" aria-hidden="true"></span>' +
        (kicker ? '<p class="ibns-kicker" id="' + esc(ctx.kickerId) + '">' + esc(kicker) + '</p>' : '') +
        '</div>';
    },

    // The whole card is one link, named by its title and described by the
    // kicker and tagline. No summary, highlights, prices or offer wording.
    // Colours (D-i5): only the three re-checked --ibl-card-* properties, as
    // on the page (the accent is the contrast-adjusted mark); a card without
    // valid colours writes no style and keeps the house palette.
    card: function (launch, ctx) {
      var described = [];
      if (kickerText(launch)) described.push(ctx.kickerId);
      if (launch.tagline) described.push(ctx.taglineId);
      var colours = ctx.colours;
      return '<li class="ibns-item" data-ibns-item="' + esc(launch.id) + '">' +
        '<a class="ibns-card' + (colours ? '' : ' ibns-card--house') + '" href="' + esc(ctx.href) + '"' +
        (colours ? ' style="' + esc(colourStyleValue(colours, colours.mark)) + '"' : '') +
        ' aria-labelledby="' + esc(ctx.titleId) + '"' +
        (described.length ? ' aria-describedby="' + esc(described.join(' ')) + '"' : '') + '>' +
        templates.slot(launch, ctx) +
        '<div class="ibns-panel">' +
        '<div class="ibns-text">' +
        '<h3 class="ibns-title" id="' + esc(ctx.titleId) + '">' + esc(launch.title) + '</h3>' +
        (launch.tagline ? '<p class="ibns-tagline" id="' + esc(ctx.taglineId) + '">' + esc(launch.tagline) + '</p>' : '') +
        '</div>' +
        '<span class="ibns-cta"><span>' + esc(ctx.buttonLabel) + '</span>' + ARROW + '</span>' +
        '</div>' +
        '</a></li>';
    },

    // One collage tile (D-i6): the card slot's <picture> at tile size - the
    // portrait banner on phones, the banner wider - drawn with object-fit:
    // cover. Decoration only: alt="", and no data-ibns-img (bandChanged
    // matches those to this.arts by index).
    tile: function (launch, sizes) {
      var F = feed();
      return '<div class="ibns-all-tile">' +
        '<picture>' +
        '<source media="' + PHONE + '" srcset="' + esc(F.imageSrcset(launch.portraitBanner.url, TILE_WIDTHS)) + '"' +
        ' sizes="' + esc(sizes.portrait) + '">' +
        '<img class="ibns-all-img" src="' + esc(F.imageUrl(launch.banner.url, TILE_SRC_WIDTH)) + '"' +
        ' srcset="' + esc(F.imageSrcset(launch.banner.url, TILE_WIDTHS)) + '"' +
        ' sizes="' + esc(sizes.landscape) + '"' +
        ' alt="" loading="lazy" decoding="async">' +
        '</picture></div>';
    },

    // The View all card (D-i6), the Promotions collage card: one link to the
    // New In page without a hash, named "View all N launches". The 3x3 grid is
    // aria-hidden; tiles beyond the launches given stay empty (cocoa).
    viewAll: function (ctx) {
      var grid = '';
      for (var i = 0; i < COLLAGE_TILES; i++) {
        grid += ctx.tiles[i] ? templates.tile(ctx.tiles[i], ctx.sizes) : '<div class="ibns-all-tile"></div>';
      }
      return '<li class="ibns-item ibns-item--all" data-ibns-all>' +
        '<a class="ibns-all" href="' + esc(ctx.href) + '" aria-label="' + esc(STRINGS.viewAllName(ctx.total)) + '">' +
        '<div class="ibns-all-grid" aria-hidden="true">' + grid + '</div>' +
        '<span class="ibns-all-scrim" aria-hidden="true"></span>' +
        '<div class="ibns-all-content">' +
        '<span class="ibns-all-more">' + esc(STRINGS.more(ctx.extra)) + '</span>' +
        '<h3 class="ibns-all-title">' + esc(STRINGS.viewAll) + ' <span class="ibns-all-count">' + esc(STRINGS.count(ctx.total)) + '</span></h3>' +
        '<span class="ibns-all-cta"><span>' + esc(STRINGS.browseAll) + '</span>' + ARROW + '</span>' +
        '</div>' +
        '</a></li>';
    },

    // Built already marked, so a rebuilt row never animates in from grey.
    dots: function (pages, current) {
      var html = '';
      for (var i = 0; i < pages; i++) {
        html += '<button type="button" class="ibns-dot" data-ibns-dot="' + i + '"' +
          ' aria-label="' + esc(STRINGS.page(i + 1, pages)) + '"' +
          ' aria-current="' + (i === current ? 'true' : 'false') + '"><span></span></button>';
      }
      return html;
    }
  };

  /* ----------------------------------------------------------------------
   * 5. Slider instance
   * -------------------------------------------------------------------- */
  var uidCounter = 0;

  function readConfig(root) {
    var node = root.querySelector('[data-ibns-config]');
    if (!node) return null;
    try {
      var cfg = JSON.parse(node.textContent);
      return (cfg && typeof cfg === 'object') ? cfg : null;
    } catch (e) {
      return null;
    }
  }

  function setDisabled(btn, disabled) {
    if (btn) btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  }

  // Keyboard focus only: a mouse press focuses a link without :focus-visible.
  function keyboardFocused(el) {
    try {
      return !!(el && el.matches && el.matches(':focus-visible'));
    } catch (e) {
      return false;
    }
  }

  function Slider(root) {
    this.root = root;
    this.track = root.querySelector('[data-ibns-track]');
    this.scroller = root.querySelector('[data-ibns-scroller]');
    this.prevBtn = root.querySelector('[data-ibns-prev]');
    this.nextBtn = root.querySelector('[data-ibns-next]');
    this.dotsWrap = root.querySelector('[data-ibns-dots]');
    this.countEl = root.querySelector('[data-ibns-count]');
    this.uid = 'ibns' + (++uidCounter);
    this.cfg = null;
    this.opts = null;
    this.launches = [];
    this.arts = [];
    this.starts = [];
    this.page = 0;
    this.lock = null;
    this.settleTimer = 0;
    this.raf = 0;
    this.phone = false;
    this.bound = false;
    this.queries = [];
    this.introObserver = null;
    this.destroyed = false;
  }

  Slider.prototype.init = function () {
    this.cfg = readConfig(this.root);
    var F = feed();
    if (!this.track || !this.scroller || !this.cfg || !this.cfg.feedUrl ||
        !F || typeof F.validateFeed !== 'function' || typeof fetch !== 'function') {
      this.hide('unavailable');
      return;
    }
    this.opts = options(this.cfg);
    this.load();
  };

  Slider.prototype.load = function () {
    var self = this;
    fetch(this.cfg.feedUrl, { credentials: 'omit' })
      .then(function (res) {
        if (!res.ok) throw new Error('Launch feed HTTP ' + res.status);
        return res.json();
      })
      .then(function (json) { if (!self.destroyed) self.ingest(json); })
      .catch(function (err) {
        if (self.destroyed) return;
        warn('[IBNewInSlider] New In slider hidden: the launch feed could not be shown', err);
        self.hide('error');
      });
  };

  Slider.prototype.ingest = function (json) {
    var result = feed().validateFeed(json);
    if (!result.ok) { this.hide('rejected'); return; }
    var customer = this.cfg.customer || null;
    var visible = [];
    for (var i = 0; i < result.launches.length; i++) {
      if (cardVisible(result.launches[i], customer)) visible.push(result.launches[i]);
    }
    if (!visible.length) { this.hide('empty'); return; }
    this.render(visible);
  };

  // The homepage never shows an empty or error slider.
  Slider.prototype.hide = function (state) {
    this.root.setAttribute('hidden', '');
    this.root.setAttribute('data-ibns-state', state);
    if (this.track) {
      this.track.innerHTML = '';
      this.track.setAttribute('aria-busy', 'false');
    }
  };

  Slider.prototype.render = function (visible) {
    var opts = this.opts;
    var shown = visible.slice(0, opts.max);
    var sizes = slotSizes(opts);
    this.phone = matches(PHONE);
    this.launches = shown;
    this.arts = [];
    var slides = [];
    for (var i = 0; i < shown.length; i++) {
      var launch = shown[i];
      var art = slotArt(launch);
      var ids = this.uid + '-' + i;
      this.arts.push(art);
      slides.push(templates.card(launch, {
        art: art,
        colours: cardColours(launch),
        sizes: sizes,
        phone: this.phone,
        href: cardHref(opts, launch),
        buttonLabel: opts.buttonLabel,
        titleId: ids + '-title',
        kickerId: ids + '-kicker',
        taglineId: ids + '-tagline'
      }));
    }
    // D-i6: more visible launches than shown adds the View all card, at the
    // section's position (D-i10: third by default, after two launch cards).
    if (visible.length > shown.length) {
      slides.splice(viewAllIndex(opts.viewAllPosition, shown.length), 0, templates.viewAll({
        href: opts.pageUrl,
        total: visible.length,
        extra: visible.length - shown.length,
        tiles: collageTiles(visible, shown.length),
        sizes: tileSizes(opts)
      }));
    }
    this.track.innerHTML = slides.join('');
    this.track.setAttribute('aria-busy', 'false');
    if (this.countEl) this.countEl.textContent = STRINGS.count(visible.length);
    this.root.removeAttribute('hidden');
    this.root.setAttribute('data-ibns-state', 'ready');
    this.watchImages();
    this.bind();
    this.refresh();
    this.intro();
  };

  // Rev j: the card photos still loading shimmer in their slots until they land.
  Slider.prototype.watchImages = function () {
    if (reducedMotion()) return;
    var imgs = this.track.querySelectorAll('.ibns-slot img');
    for (var i = 0; i < imgs.length; i++) watchImage(imgs[i]);
  };

  // Rev j (D-j3): the cards rise in, one after another, when the card row
  // first comes into view (at once when it already is). Without
  // IntersectionObserver, or under reduced motion, nothing is hidden.
  Slider.prototype.intro = function () {
    if (this.introObserver || typeof window.IntersectionObserver !== 'function' || reducedMotion()) return;
    var self = this;
    this.root.setAttribute('data-ibns-intro', 'wait');
    this.introObserver = new window.IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        self.root.setAttribute('data-ibns-intro', 'in');
        self.introObserver.disconnect();
        return;
      }
    }, { threshold: 0.25 });
    this.introObserver.observe(this.scroller);
  };

  /* ---- Carousel ---- */

  Slider.prototype.perView = function () {
    if (matches(PHONE)) return this.opts.mobile;
    if (matches(TABLET)) return this.opts.tablet;
    return this.opts.desktop;
  };

  // Every slide, the View all card (D-i6) included: the page stops.
  Slider.prototype.items = function () {
    return this.track.querySelectorAll(SLIDE);
  };

  // Snap points, dots and arrows for the current width band.
  Slider.prototype.refresh = function () {
    var items = this.items();
    this.starts = pageStarts(items.length, this.perView());
    for (var i = 0; i < items.length; i++) {
      items[i].classList.toggle('is-snap', this.starts.indexOf(i) > -1);
    }
    if (this.page > this.starts.length - 1) this.page = Math.max(0, this.starts.length - 1);
    var pages = this.starts.length;
    var many = pages > 1;
    // The dot row keeps its height either way (the section CSS hides an empty
    // row), and it is only rebuilt when the page count changes.
    if (this.dotsWrap && this.dotsWrap.querySelectorAll('[data-ibns-dot]').length !== (many ? pages : 0)) {
      this.dotsWrap.innerHTML = many ? templates.dots(pages, this.page) : '';
      if (many) this.dotsWrap.removeAttribute('data-ibns-empty');
      else this.dotsWrap.setAttribute('data-ibns-empty', '');
    }
    if (this.prevBtn) this.prevBtn.hidden = !many;
    if (this.nextBtn) this.nextBtn.hidden = !many;
    this.lock = null;
    this.scrollToPage(this.page, false);
  };

  Slider.prototype.maxScroll = function () {
    return Math.max(0, (this.scroller.scrollWidth || 0) - (this.scroller.clientWidth || 0));
  };

  // The scroll offset that puts page p's first card at the track's start.
  Slider.prototype.pageTarget = function (p) {
    var item = this.items()[this.starts[p] || 0];
    if (!item || typeof item.getBoundingClientRect !== 'function') return 0;
    var offset = item.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
    return Math.max(0, Math.min(offset, this.maxScroll()));
  };

  Slider.prototype.scrollToPage = function (p, smooth) {
    var last = Math.max(0, this.starts.length - 1);
    p = p < 0 ? 0 : (p > last ? last : p);
    this.page = p;
    if (typeof this.scroller.scrollTo === 'function') {
      var animate = smooth && !reducedMotion();
      if (animate) {
        this.lock = { page: p, until: now() + LOCK_MS };
        this.settleAfterLock();
      }
      this.scroller.scrollTo({ left: this.pageTarget(p), behavior: animate ? 'smooth' : 'auto' });
    }
    this.syncControls();
  };

  // A swipe can interrupt the smooth scroll and come to rest on another page
  // inside the lock, and onScroll drops those events, so once the lock runs out
  // the controls read the real position again. Without this the dots and
  // arrows stayed on a page the slider never reached (PR #158 review).
  Slider.prototype.settleAfterLock = function () {
    var self = this;
    if (this.settleTimer) clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(function () {
      self.settleTimer = 0;
      self.onScroll();
    }, LOCK_MS + 50);
  };

  Slider.prototype.step = function (dir) {
    var p = this.page + dir;
    if (p < 0 || p > this.starts.length - 1) return;
    this.scrollToPage(p, true);
  };

  Slider.prototype.nearestPage = function () {
    var last = this.starts.length - 1;
    if (last <= 0) return 0;
    var x = this.scroller.scrollLeft || 0;
    if (x >= this.maxScroll() - 2) return last;
    var best = 0;
    var bestDistance = Infinity;
    for (var p = 0; p <= last; p++) {
      var d = Math.abs(x - this.pageTarget(p));
      if (d < bestDistance) { bestDistance = d; best = p; }
    }
    return best;
  };

  Slider.prototype.onScroll = function () {
    var self = this;
    function update() {
      self.raf = 0;
      var lock = self.lock;
      if (lock) {
        var arrived = Math.abs((self.scroller.scrollLeft || 0) - self.pageTarget(lock.page)) <= 2;
        if (!arrived && now() < lock.until) return;
        self.lock = null;
      }
      self.page = self.nearestPage();
      self.syncControls();
    }
    if (typeof window.requestAnimationFrame !== 'function') { update(); return; }
    if (!this.raf) this.raf = window.requestAnimationFrame(update);
  };

  Slider.prototype.syncControls = function () {
    var last = this.starts.length - 1;
    setDisabled(this.prevBtn, this.page <= 0);
    setDisabled(this.nextBtn, this.page >= last);
    if (!this.dotsWrap) return;
    var dots = this.dotsWrap.querySelectorAll('[data-ibns-dot]');
    for (var i = 0; i < dots.length; i++) {
      dots[i].setAttribute('aria-current', i === this.page ? 'true' : 'false');
    }
  };

  // One listener per control, bound once. Width bands come from matchMedia.
  Slider.prototype.bind = function () {
    if (this.bound) return;
    this.bound = true;
    var self = this;
    if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.step(-1); });
    if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.step(1); });
    if (this.dotsWrap) {
      this.dotsWrap.addEventListener('click', function (e) {
        var dot = closest(e.target, '[data-ibns-dot]');
        if (dot) self.scrollToPage(parseInt(dot.getAttribute('data-ibns-dot'), 10), true);
      });
    }
    this.onScrollHandler = function () { self.onScroll(); };
    this.scroller.addEventListener('scroll', this.onScrollHandler, { passive: true });
    this.onFocusIn = function (e) { self.focusCard(e.target); };
    this.scroller.addEventListener('focusin', this.onFocusIn);
    this.watch(PHONE);
    this.watch(TABLET);
  };

  // Tabbing onto a card brings its page into view. Only page starts snap, so
  // the browser's own focus scroll would snap back and leave a card focused in
  // the peek. A mouse press never moves the slider under the pointer.
  Slider.prototype.focusCard = function (target) {
    var item = closest(target, SLIDE);
    if (!item || !keyboardFocused(target)) return;
    var items = this.items();
    var index = -1;
    for (var i = 0; i < items.length; i++) {
      if (items[i] === item) { index = i; break; }
    }
    if (index < 0) return;
    var p = 0;
    for (var k = 0; k < this.starts.length; k++) {
      if (this.starts[k] <= index) p = k;
    }
    if (p !== this.page) this.scrollToPage(p, true);
  };

  Slider.prototype.watch = function (query) {
    if (typeof window.matchMedia !== 'function') return;
    var mql = window.matchMedia(query);
    var self = this;
    var onChange = function () { self.bandChanged(); };
    if (typeof mql.addEventListener === 'function') mql.addEventListener('change', onChange);
    else if (typeof mql.addListener === 'function') mql.addListener(onChange);
    this.queries.push({ mql: mql, fn: onChange });
  };

  // A width band changed: the page count changes with the cards per view, and
  // each <img> alt follows the shape now in effect.
  Slider.prototype.bandChanged = function () {
    var phone = matches(PHONE);
    if (phone !== this.phone) {
      this.phone = phone;
      var imgs = this.track.querySelectorAll('[data-ibns-img]');
      for (var i = 0; i < imgs.length && i < this.arts.length; i++) {
        imgs[i].setAttribute('alt', (phone ? this.arts[i].portrait : this.arts[i].landscape).alt);
      }
    }
    this.refresh();
  };

  /* PR #158 CodeRabbit (2026-10-16): theme editor only. A feed that lands after
     the section was unloaded must not render the detached slider, or its bind()
     would add listeners and an observer nothing removes. */
  Slider.prototype.destroy = function () {
    this.destroyed = true;
    for (var i = 0; i < this.queries.length; i++) {
      var q = this.queries[i];
      if (typeof q.mql.removeEventListener === 'function') q.mql.removeEventListener('change', q.fn);
      else if (typeof q.mql.removeListener === 'function') q.mql.removeListener(q.fn);
    }
    this.queries = [];
    if (this.onScrollHandler) this.scroller.removeEventListener('scroll', this.onScrollHandler);
    if (this.onFocusIn) this.scroller.removeEventListener('focusin', this.onFocusIn);
    if (this.raf && typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(this.raf);
    this.raf = 0;
    if (this.settleTimer) clearTimeout(this.settleTimer);
    this.settleTimer = 0;
    if (this.introObserver) this.introObserver.disconnect();
  };

  /* ----------------------------------------------------------------------
   * 6. Bootstrap (defer-safe; mirrors ib-launches.js)
   * -------------------------------------------------------------------- */
  var api = {
    watchImage: watchImage,
    STRINGS: STRINGS,
    cardVisible: cardVisible,
    viewAllIndex: viewAllIndex,
    cardColours: cardColours,
    colourStyleValue: colourStyleValue,
    options: options,
    kickerText: kickerText,
    slotArt: slotArt,
    slotSizes: slotSizes,
    tileSizes: tileSizes,
    collageTiles: collageTiles,
    pageStarts: pageStarts,
    templates: templates,
    Slider: Slider,
    boot: boot,
    _sliders: []
  };

  function boot() {
    var roots = document.querySelectorAll('[data-ibns-root]');
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      if (root.getAttribute('data-ibns-booted')) continue;
      root.setAttribute('data-ibns-booted', '1');
      var slider = new Slider(root);
      api._sliders.push(slider);
      slider.init();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    setTimeout(boot, 0);
  }
  // Theme editor only (Shopify.designMode): boot a re-rendered section and let
  // go of a removed one. On the storefront no document-level listener is added.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function () { boot(); });
    document.addEventListener('shopify:section:unload', function (e) {
      var target = e && e.target;
      api._sliders = api._sliders.filter(function (slider) {
        if (target && target.contains && target.contains(slider.root)) { slider.destroy(); return false; }
        return true;
      });
    });
  }

  return api;
})();
